import { DataTypeSearchCombo, FieldMatchNodeData, SearchCombo } from 'modules/workflows/types';
import { TextSegments } from 'ui/workflows/flowchart';
import { AND, NOT, OR } from 'utils/combinatorTypes';
import { getESDataTypeDisplayName } from '../../dataUtils';

const groupCombinatorTextMap = {
  [AND]: {
    initial: 'Must have',
    joining: 'and have',
  },
  [OR]: {
    initial: 'Must have',
    joining: 'or have',
  },
  [NOT]: {
    initial: "Can't have",
    joining: 'nor have',
  },
};

const propertyCombinatorTextMap = {
  [AND]: {
    initial: 'all of',
    joining: 'and',
  },
  [OR]: {
    initial: 'any of',
    joining: 'or',
  },
  [NOT]: {
    initial: 'none of',
    joining: 'nor',
  },
};

type CombinatorText = string;
type DataType = string;

/**
 * Gets the description of a property group with queries
 *
 * @returns
 * The property group description
 * EG: "any of 3 queries on Contact Property"
 * EG2: "or 1 query on Custom Event"
 */
const getPropertyQueriesDescription = ({
  combinatorText,
  dataType,
  numQueries = 0,
}: {
  combinatorText: CombinatorText;
  dataType?: DataType;
  numQueries?: number;
}): string => {
  const queryText = `${numQueries} quer${numQueries === 1 ? 'y' : 'ies'}`;
  const dataTypeText = getESDataTypeDisplayName(dataType);
  return `    ${combinatorText} ${queryText} on ${dataTypeText}`;
};

/**
 * Gets the description of a search group
 *
 * @returns
 * A single search group
 * `
 * Must have:
 *   any of 3 queries on Contact Property
 *   or 1 query on Custom Event
 * `
 */
const getSearchGroupText = (
  searchCombo: SearchCombo,
  { combinatorText }: { combinatorText: CombinatorText },
): string => {
  const { searchQueries = [], combinator = AND } = searchCombo || {};
  const { initial = '', joining = '' } = propertyCombinatorTextMap[combinator] || {};

  return (searchQueries as DataTypeSearchCombo[])
    .reduce(
      (acc: string[], query, i) => [
        ...acc,
        // Adds line with outer combinator text when needed
        // EG: "Must have"/"and have"/"Can't have" etc
        ...(i === 0 ? [`${combinatorText}:`] : []),
        // Adds the description of the property and its queries
        // EG: "any of 1 query on Contact Property"
        getPropertyQueriesDescription({
          combinatorText: i === 0 ? initial : joining,
          dataType: query?.dataType,
          numQueries: query?.searchCombo?.searchQueries?.length,
        }),
      ],
      [],
    )
    .join('\n');
};

/**
 * Gets the tooltip for the fields match text
 *
 * @returns
 * The description of the fields match criteria
 * `
 * Must have:
 *   any of 3 queries on Contact Property
 *   or 1 query on Custom Event
 *
 * or have:
 *   none of 2 queries on Purchase
 * `
 */
const getFieldsMatchTooltip = (searchCombo?: FieldMatchNodeData['searchQuery']): string => {
  const { combinator = AND, searchQueries = [] } = searchCombo || {};
  const { initial, joining } = groupCombinatorTextMap[combinator] || {};

  return searchQueries
    .map((query, index) =>
      getSearchGroupText(query as SearchCombo, { combinatorText: index === 0 ? initial : joining }),
    )
    .join('\n\n');
};

/**
 * Gets the text segments needed for flowchart to render the fields match text
 */
export const getTextSegments = (nodeData: FieldMatchNodeData): TextSegments => ({
  nodeDescBottom: {
    segments: [
      { text: 'Matches ' },
      {
        text: `${nodeData?.searchQuery?.searchQueries?.length || 0} Criteria`,
        tooltipText: getFieldsMatchTooltip(nodeData?.searchQuery),
      },
    ],
  },
});
